import { createContext, useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react'
import { SessionDataContext } from './SessionDataContext'
import { getTelemetryEventsBySession } from '../../workspace/service/telemetryService'

export const TelemetryDataContext = createContext()

// Times are local Virginia time (EST before DST, EDT after March 8, 2026).
// EST = UTC-5, EDT = UTC-4. End times have a 30-minute buffer.
// Keys are composite: `{sectionId}-{YYYY-MM-DD}` to disambiguate
// repeated section IDs across different deployment dates.
const SESSION_TIME_WINDOWS = {
          // deployment_1 — Instructor A am (Python) — 2/26/2026, 9:30-10:45 AM EST
          '13371-2026-02-26': {
            start: new Date('2026-02-26T14:30:00Z').getTime(),
            end:   new Date('2026-02-26T16:15:00Z').getTime(),
          },
          // deployment_2 — Instructor A pm (Python) — 2/26/2026, 2:00-3:15 PM EST
          '21938-2026-02-26': {
            start: new Date('2026-02-26T19:00:00Z').getTime(),
            end:   new Date('2026-02-26T20:45:00Z').getTime(),
          },
          // deployment_3 — Instructor B am (Python) — 3/18/2026, 9:05-9:55 AM EDT
          '13370-2026-03-18': {
            start: new Date('2026-03-18T13:05:00Z').getTime(),
            end:   new Date('2026-03-18T14:25:00Z').getTime(),
          },
          // deployment_4 — Instructor B am (Python) — 4/27/2026, 9:05-9:55 AM EDT
          '13370-2026-04-27': {
            start: new Date('2026-04-27T13:05:00Z').getTime(),
            end:   new Date('2026-04-27T14:25:00Z').getTime(),
          },
          // deployment_5 — Instructor B pm (Python) — 4/27/2026, 12:20-1:10 PM EDT
          '13372-2026-04-27': {
            start: new Date('2026-04-27T16:20:00Z').getTime(),
            end:   new Date('2026-04-27T17:40:00Z').getTime(),
          },
          // deployment_6 — Instructor C pm (C) — 4/27/2026, 5:30-6:45 PM EDT
          '13369-2026-04-27': {
            start: new Date('2026-04-27T21:30:00Z').getTime(),
            end:   new Date('2026-04-27T23:15:00Z').getTime(),
          },
          // deployment_7 — Instructor A am (Python) — 4/28/2026, 9:30-10:45 AM EDT
          '13371-2026-04-28': {
            start: new Date('2026-04-28T13:30:00Z').getTime(),
            end:   new Date('2026-04-28T15:15:00Z').getTime(),
          },
          // deployment_8 — Instructor A pm (Python) — 4/28/2026, 2:00-3:15 PM EDT
          '21938-2026-04-28': {
            start: new Date('2026-04-28T18:00:00Z').getTime(),
            end:   new Date('2026-04-28T19:45:00Z').getTime(),
          },
        }

// Picks the deployment time-window (if any) that contains the most events, and trims
// `data` to it - for any sectionId outside SESSION_TIME_WINDOWS this always returns
// `data` unfiltered.
function applyTimeWindow(data, sectionId) {
  const candidates = Object.entries(SESSION_TIME_WINDOWS)
    .filter(([key]) => key.startsWith(`${sectionId}-`))
    .map(([key, win]) => ({ key, ...win }))

  let window = null
  if (candidates.length > 0) {
    const allEvents = Object.values(data).flat()
    let bestCount = 0
    for (const c of candidates) {
      const count = allEvents.filter(e => e.timestamp >= c.start && e.timestamp <= c.end).length
      if (count > bestCount) {
        bestCount = count
        window = c
      }
    }
  }

  if (!window) return data

  const filtered = {}
  for (const [userId, events] of Object.entries(data)) {
    const inRange = events.filter(e => e.timestamp >= window.start && e.timestamp <= window.end)
    if (inRange.length > 0) filtered[userId] = inRange
  }
  return filtered
}

export const TelemetryDataProvider = ({ sectionId, live = false, children }) => {
  const { sessionId } = useContext(SessionDataContext)

  const [eventsByUser, setEventsByUser] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // Only the very first fetch (per sessionId) should show the loading spinner - a
  // background poll or manual refresh should swap eventsByUser in place without
  // flashing the whole dashboard back to "Loading dashboard data..." and losing
  // whatever the instructor had selected/scrolled to.
  const hasLoadedOnce = useRef(false)

  const fetchTelemetry = useCallback(async () => {
    if (!sessionId) return
    if (!hasLoadedOnce.current) setLoading(true)
    setError(null)
    try {
      const data = await getTelemetryEventsBySession(sessionId)
      setEventsByUser(applyTimeWindow(data, sectionId))
    } catch (err) {
      console.error('Error fetching session telemetry:', err)
      setError(err)
    } finally {
      if (!hasLoadedOnce.current) {
        setLoading(false)
        hasLoadedOnce.current = true
      }
    }
  }, [sessionId, sectionId])

  useEffect(() => {
    hasLoadedOnce.current = false
    fetchTelemetry()
    // fetchTelemetry is stable for a given [sessionId, sectionId] pair (see its own
    // useCallback deps) - re-running this effect on sessionId/sectionId change alone,
    // not on every fetchTelemetry identity change, is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sectionId])

  // Auto-refresh while `live` is true (an instructor has the Analytics/Replay sub-tab
  // open during an active session - see InstructorDashboardPanel.jsx) - re-fetch on an
  // interval and update eventsByUser in place rather than resetting `loading`. Paused
  // whenever the browser tab itself isn't visible. Client telemetry batches every 5s
  // (see TelemetryContext.js), so polling faster than that buys nothing.
  //
  // Fires once immediately (not just after the first interval tick) - otherwise an
  // instructor who spent ten minutes on the Live sub-tab (where `live` is false, so
  // nothing was polling) and then switches to Analytics would see up to 20s of a
  // ten-minute-stale, possibly-empty snapshot before the first refresh lands.
  useEffect(() => {
    if (!live) return
    const poll = () => {
      if (document.visibilityState === 'visible') fetchTelemetry()
    }
    poll()
    const interval = setInterval(poll, 20000)
    return () => clearInterval(interval)
  }, [live, fetchTelemetry])

  // ─── Computed stats ────────────────────────────────────────

  const stats = useMemo(() => computeTelemetryStats(eventsByUser, sectionId), [eventsByUser, sectionId])

  // Get events for a specific user (for ReplayModal)
  const getEventsForUser = (userId) => {
    return eventsByUser[userId] || eventsByUser[String(userId)] || []
  }

  return (
    <TelemetryDataContext.Provider value={{
      eventsByUser,
      loading,
      error,
      stats,
      getEventsForUser,
      refresh: fetchTelemetry,
    }}>
      {children}
    </TelemetryDataContext.Provider>
  )
}

// ─── Helpers ──────────────────────────────────────────────

// The Monitor's class statistics from one session's telemetry (eventsByUser): counts, pass
// rate, error and progress timelines, latest conversations, per-student summaries. The
// experiment page's Overview runs it on every condition's telemetry merged.
// `startedAt` (ms): when the exercise started, for a caller that knows it - the experiment
// Overview, from its shared timer. The minute axes then count from there; without it, from the
// earliest SESSION_START, as the Monitor always has.
export function computeTelemetryStats(eventsByUser, sectionId, { startedAt } = {}) {
  const userIds = Object.keys(eventsByUser)
  if (userIds.length === 0) {
    return {
      totalStudents: 0,
      finishedStudents: 0,
      passRate: 0,
      aiUsagePercent: 0,
      errorDistribution: [],
      latestConversations: {
        totalConversations: 0,
        uniqueConversations: 0,
        recentMessages: [],
      },
      studentSummaries: {},
    }
  }

  let totalFinished = 0
  let totalUsingAi = 0
  let validStudents = 0
  const allErrors = []
  const allChatMessages = []
  const studentSummaries = {}
  let sessionStartTime = Infinity

  userIds.forEach((userId) => {
    const events = eventsByUser[userId]
    if (!events || events.length === 0) return

    // Skip sessions under 15 seconds or with only SESSION_START events
    const first = events[0]?.timestamp
    const last = events[events.length - 1]?.timestamp
    if (!first || !last || (last - first) < 15000) return
    if (!events.some(e => e.type !== 'SESSION_START')) return

    validStudents++

    // ── Per-student stats ──
    const firstTimestamp = events[0]?.timestamp

    // Match ReplayModal: truncate at first all-tests-passed event
    const allPassedIndex = events.findIndex(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false
      const { passed_count, total_tests } = e.payload || {}
      return passed_count === total_tests && total_tests > 0
    })

    const relevantEvents = allPassedIndex !== -1
      ? events.slice(0, allPassedIndex + 1)
      : events

    const lastTimestamp = relevantEvents[relevantEvents.length - 1]?.timestamp
    const durationSeconds = firstTimestamp && lastTimestamp
      ? Math.round((lastTimestamp - firstTimestamp) / 1000)
      : 0

    // Track earliest session start for error distribution bucketing
    const sessionStart = events.find(e => e.type === 'SESSION_START')
    if (sessionStart && sessionStart.timestamp < sessionStartTime) {
      sessionStartTime = sessionStart.timestamp
    }

    // Chat queries
    const chatQueries = events.filter(e => e.type === 'CHAT_QUERY')
    const chatResponses = events.filter(e => e.type === 'CHAT_RESPONSE')
    const chatCount = chatQueries.length

    if (chatCount > 0) totalUsingAi++

    // Test results — find the last TEST_CASE_RESULT event
    const testResultEvents = events.filter(e => e.type === 'TEST_CASE_RESULT')
    let testsPassed = 0
    let testsTotal = 0
    let allPassed = false

    if (testResultEvents.length > 0) {
      const lastResult = testResultEvents[testResultEvents.length - 1]
      testsTotal = lastResult.payload?.total_tests || 0
      testsPassed = lastResult.payload?.passed_count || 0
      allPassed = testsTotal > 0 && testsPassed === testsTotal
    }

    if (allPassed) totalFinished++

    // Errors
    const errors = events.filter(e => e.type === 'TERMINAL_ERROR')
    errors.forEach(e => {
      // Extract real error type from message since getErrorType may return UnknownError
      const payloadType = (e.payload?.type || '').toLowerCase()
      const message = e.payload?.message || ''
      // Python tracebacks have the error type at the end, e.g. "TypeError: ..."
      const tracebackMatch = message.match(/(\w+Error):/m)
      const realType = tracebackMatch ? tracebackMatch[1].toLowerCase() : payloadType

      console.log('Error parsed:', { payloadType, realType, tracebackMatch: tracebackMatch?.[1], message: message.slice(-80) })

      allErrors.push({
        timestamp: e.timestamp,
        errorType: realType,
        userId,
      })
    })

    // Chat messages for latest conversations
    chatQueries.forEach(e => {
      allChatMessages.push({
        content: e.payload?.text || e.payload?.message || e.payload?.content || '',
        senderId: userId,
        timestamp: e.timestamp,
        type: 'query',
      })
    })

    chatResponses.forEach(e => {
      allChatMessages.push({
        content: e.payload?.text || e.payload?.message || e.payload?.content || '',
        senderId: 'ai',
        timestamp: e.timestamp,
        type: 'response',
      })
    })

    // Last active = last event timestamp (from all events, not truncated)
    const trueLastTimestamp = events[events.length - 1]?.timestamp
    const lastActive = trueLastTimestamp ? new Date(trueLastTimestamp).toISOString() : null

    studentSummaries[userId] = {
      duration: durationSeconds,
      chatCount,
      testsPassed,
      testsTotal,
      allPassed,
      lastActive,
    }
  })

  // ── Pass rate ──
  const passRate = validStudents > 0
    ? Math.round((totalFinished / validStudents) * 100)
    : 0

  // ── AI usage ──
  const aiUsagePercent = validStudents > 0
    ? Math.round((totalUsingAi / validStudents) * 100)
    : 0

  // The exercise's own start when the caller has it: one student's page can open before it (in
  // E1-S3 a Start, Reset and Start put one SESSION_START 5 minutes early and shifted the plot).
  if (Number.isFinite(startedAt)) sessionStartTime = startedAt

  // If no SESSION_START found, fall back to earliest event timestamp
  if (sessionStartTime === Infinity) {
    const allTimestamps = Object.values(eventsByUser).flatMap(events => events.map(e => e.timestamp)).filter(Boolean)
    if (allTimestamps.length > 0) {
      sessionStartTime = Math.min(...allTimestamps)
    }
  }

  // ── Error distribution (bucketed by minute) ──
  // For section 21938, remove the earliest runtime error (artifact from session start)
  let filteredErrors = [...allErrors].sort((a, b) => a.timestamp - b.timestamp)
  if (sectionId === '21938') {
    const firstRuntimeIdx = filteredErrors.findIndex(e => {
      const t = (e.errorType || '').toLowerCase()
      return t.includes('name') || t.includes('attribute') || t.includes('index') || t.includes('value') || t.includes('recursion') ||
        (!t.includes('syntax') && !t.includes('indentation') && !t.includes('logic') && !t.includes('test') && !t.includes('assert'))
    })
    if (firstRuntimeIdx !== -1) {
      filteredErrors.splice(firstRuntimeIdx, 1)
    }
  }
  const errorDistribution = buildErrorDistribution(filteredErrors, sessionStartTime)
  console.log('allErrors:', allErrors.length, 'sessionStartTime:', sessionStartTime, 'errorDistribution:', errorDistribution)

  // ── Latest conversations ──
  allChatMessages.sort((a, b) => b.timestamp - a.timestamp)
  const uniqueUserChats = new Set(allChatMessages.filter(m => m.type === 'query').map(m => m.senderId))

  const latestConversations = {
    totalConversations: allChatMessages.filter(m => m.type === 'query').length,
    uniqueConversations: uniqueUserChats.size,
    recentMessages: allChatMessages.slice(0, 20).map(m => ({
      content: m.content,
      senderId: m.senderId,
      timestamp: new Date(m.timestamp).toISOString(),
      type: m.type,
    })),
  }

  // ── AI usage & pass rate over time (same time range as error distribution) ──
  const totalMinutes = errorDistribution.length > 0 ? errorDistribution.length - 1 : 0
  const progressTimeline = buildProgressTimeline(eventsByUser, sessionStartTime, totalMinutes)

  return {
    totalStudents: validStudents,
    finishedStudents: totalFinished,
    passRate,
    aiUsagePercent,
    errorDistribution,
    progressTimeline,
    latestConversations,
    studentSummaries,
  }
}

function buildErrorDistribution(errors, sessionStartTime) {
  if (errors.length === 0 || sessionStartTime === Infinity) return []

  // Find the time range
  const maxTimestamp = Math.max(...errors.map(e => e.timestamp))
  const totalMinutes = Math.ceil((maxTimestamp - sessionStartTime) / 60000) || 10

  // Build minute buckets
  const buckets = []
  for (let i = 0; i <= totalMinutes; i++) {
    buckets.push({
      time: `${i}m`,
      syntax: 0,
      runtime: 0,
      logic: 0,
      test_failed: 0,
    })
  }

  // Categorize errors into buckets
  errors.forEach(e => {
    const minuteIndex = Math.floor((e.timestamp - sessionStartTime) / 60000)
    if (minuteIndex < 0 || minuteIndex >= buckets.length) return

    const type = (e.errorType || '').toLowerCase()
    if (type.includes('syntax') || type.includes('indentation')) {
      buckets[minuteIndex].syntax++
    } else if (type.includes('name') || type.includes('attribute') || type.includes('index') || type.includes('value') || type.includes('recursion')) {
      buckets[minuteIndex].runtime++
    } else if (type.includes('logic')) {
      buckets[minuteIndex].logic++
    } else if (type.includes('test') || type.includes('assert')) {
      buckets[minuteIndex].test_failed++
    } else {
      buckets[minuteIndex].runtime++ // default bucket
    }
  })

  return buckets
}

function buildProgressTimeline(eventsByUser, sessionStartTime, totalMinutes) {
  if (sessionStartTime === Infinity || totalMinutes <= 0) return []

  // Collect all relevant events with timestamps
  const aiEvents = []
  const passEvents = []
  const totalStudents = Object.keys(eventsByUser).length

  Object.entries(eventsByUser).forEach(([userId, events]) => {
    if (!events || events.length === 0) return

    // AI queries
    events.filter(e => e.type === 'CHAT_QUERY').forEach(e => {
      aiEvents.push({ timestamp: e.timestamp, userId })
    })

    // Track first time each student passes all tests
    const firstAllPass = events.find(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false
      const { passed_count, total_tests } = e.payload || {}
      return passed_count === total_tests && total_tests > 0
    })
    if (firstAllPass) {
      passEvents.push({ timestamp: firstAllPass.timestamp, userId })
    }
  })

  // Build minute buckets (same count as error distribution)
  const buckets = []
  for (let i = 0; i <= totalMinutes; i++) {
    buckets.push({
      time: `${i}m`,
      aiQueries: 0,
      cumulativeAiQueries: 0,
      passRate: 0,
      studentsPassed: 0,
    })
  }

  // AI queries per minute
  aiEvents.sort((a, b) => a.timestamp - b.timestamp)
  aiEvents.forEach(e => {
    const idx = Math.floor((e.timestamp - sessionStartTime) / 60000)
    if (idx >= 0 && idx < buckets.length) {
      buckets[idx].aiQueries++
    }
  })

  // Cumulative AI queries
  let cumAi = 0
  buckets.forEach(b => {
    cumAi += b.aiQueries
    b.cumulativeAiQueries = cumAi
  })

  // Cumulative pass rate
  passEvents.sort((a, b) => a.timestamp - b.timestamp)
  let passedSoFar = 0
  let passIdx = 0
  buckets.forEach((b, i) => {
    const bucketEnd = sessionStartTime + (i + 1) * 60000
    while (passIdx < passEvents.length && passEvents[passIdx].timestamp < bucketEnd) {
      passedSoFar++
      passIdx++
    }
    b.studentsPassed = passedSoFar
    b.passRate = totalStudents > 0 ? Math.round((passedSoFar / totalStudents) * 100) : 0
  })

  return buckets
}